import React, { useEffect, useId, useRef, useState } from "react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

function ResultsMetricPickerButton({
  buttonLabel,
  clearLabel,
  emptyLabel,
  metrics,
  selectedMetricKeys,
  hasCustomSelection,
  onToggleMetric,
  onClear,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const baseId = useId();

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDownOutside = (event) => {
      if (containerRef.current?.contains(event.target)) {
        return;
      }

      setOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDownOutside);
    document.addEventListener("touchstart", handlePointerDownOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDownOutside);
      document.removeEventListener("touchstart", handlePointerDownOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className={`small-outlined-button !min-w-[44px] !relative !flex !min-h-0 !flex-row !items-center !gap-1.5 !px-2.5 !py-1 whitespace-nowrap text-xs ${
          hasCustomSelection ? "!border-primary !bg-primary !text-greyscale-1" : ""
        }`}
        aria-label={buttonLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={buttonLabel}
        onClick={() => setOpen((current) => !current)}
      >
        <EditOutlinedIcon
          className="fill-current"
          sx={{ fontSize: 16 }}
          style={{ width: 16, height: 16 }}
          aria-hidden="true"
        />
        <span className="text-xs leading-none">{buttonLabel}</span>
      </button>

      {open ? (
        <div className="absolute right-0 top-full z-20 mt-2 w-72 max-w-[calc(100vw-2rem)]">
          <div className="popover !w-auto !p-0 overflow-hidden">
            <div className="flex items-center justify-between border-b border-greyscale-1 px-4 py-3">
              <h4 className="text-sm font-semibold text-typography">
                {buttonLabel}
              </h4>

              {hasCustomSelection ? (
                <button
                  type="button"
                  className="text-xs font-semibold text-primary transition-colors hover:text-primary/80"
                  onClick={onClear}
                >
                  {clearLabel}
                </button>
              ) : null}
            </div>

            {metrics.length ? (
              <fieldset className="flex flex-col gap-2 px-4 py-3">
                <legend className="sr-only">{buttonLabel}</legend>
                {metrics.map((metric) => {
                  const inputId = `${baseId}-${metric.key}`;
                  const checked = selectedMetricKeys.includes(metric.key);

                  return (
                    <label
                      key={metric.key}
                      htmlFor={inputId}
                      className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-greyscale-0.5"
                    >
                      <input
                        id={inputId}
                        type="checkbox"
                        className="choice-circle-input"
                        checked={checked}
                        onChange={() => onToggleMetric(metric.key)}
                      />
                      <span className="min-w-0 flex-1 text-sm text-typography">
                        {metric.label}
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            ) : (
              <p className="px-4 py-3 text-sm text-greyscale-3">{emptyLabel}</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ResultsMetricPickerButton;
